import {
  apiRequest
} from './client';

/**
 * Ozow's browser redirect back to /pay/success carries the same signed field
 * set as its server-to-server notification, including a Hash that cannot be
 * produced without the merchant private key.
 *
 * That makes the redirect a legitimate SECOND settlement path, not a shortcut:
 * BMS verifies the hash exactly as it does for the webhook, so a forged
 * redirect is rejected the same way a forged notification would be.
 *
 * Why this exists: until 2026-07-31 the webhook was the ONLY thing that could
 * settle a payment. On 2026-07-30 a hash bug caused Ozow's notification to be
 * rejected, and a real R5 payment sat PENDING permanently - money taken, no
 * record, and no admin action anywhere able to reconcile it. A single dropped
 * callback should not be able to lose a customer's payment.
 */
export interface OzowRedirectParams {
  SiteCode?: string
  TransactionId?: string
  TransactionReference?: string
  Amount?: string
  Status?: string
  Optional1?: string
  Optional2?: string
  Optional3?: string
  Optional4?: string
  Optional5?: string
  CurrencyCode?: string
  IsTest?: string
  StatusMessage?: string
  BankName?: string
  Hash?: string
}

/**
 * Replays Ozow's redirect parameters into the normal webhook endpoint.
 *
 * Deliberately posts to the SAME handler the real notification uses rather than
 * adding a parallel code path - the hash verification and the settlement logic
 * stay in one place, so they cannot drift apart.
 *
 * Safe to call when the webhook has already settled the payment:
 * PaymentService.confirmOrFail short-circuits on any terminal status
 * (COMPLETED / FAILED / CANCELLED) and returns `already_terminal` without
 * re-applying side effects.
 */
export const settleFromOzowRedirect = async (
  params: OzowRedirectParams
): Promise<void> => {

  await apiRequest<unknown>('/webhooks/ozow', {
    method: 'POST',
    body: JSON.stringify(params)
  });
};
